/**********************
 * Uploading pictures *
 *********************/

import axios from "@/shared/scripts/AutoRefreshAxios";
import store from "../store";
import { Picture } from "@/shared/interfaces/Picture.interface";
import { i18n } from "@/shared/scripts/helpers/translation/i18n";
import { app } from "@/main";

function delay(milliseconds: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
}

export function persistPicturesOrder() : Promise<boolean> {
    // if classifiedId is not set, quit
    const classifiedId: number|null = store.getters["FormModule/getClassifiedId"];
    if (classifiedId == null) {
        return Promise.resolve(false);
    }

    // API settings
    const baseUrl: string = process.env.VUE_APP_CLASSIFIEDS_MANAGEMENT_URL || "";
    const uri = `/classifieds/${classifiedId}/photos`;
    const version = "1";
    const method = "put";

    // get ordering if uploaded pictures
    const pictures = store.getters["PicturesModule/getPictures"];
    const picturesOrder: Array<string|null> = pictures.map((picture: Picture) => { return picture.url.substring(0, 5) !== "blob:" ? picture.id : null;});

    // because of an API limitation, we need to wait that ALL pictures are fully uploaded before persisting the order
    if(picturesOrder.filter((item: string|null) => item).length < pictures.length) {
        return Promise.resolve(false);
    }

    const currentCustomer = store.getters["CustomersModule/getCurrentCustomer"];

    const headers: Record<string, unknown> = {
        "Accept": "application/json",
        "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
    };

    if(currentCustomer) {
        headers["customerId"] = currentCustomer.id;
    }

    return axios({
        method: method,
        url: baseUrl + version + uri,
        data: picturesOrder.filter((item: string|null) => item), // filter array to remove null items
        headers: headers
    }).then(() => {
        store.commit("MyClassifiedsModule/updateClassifiedStore", {"pictures": pictures, "id": classifiedId});
        return true;
    }).catch(error => {
        throw new Error(error);
    });
}

export async function uploadPicture(picture: Picture, file: File): Promise<boolean> {
    // if classifiedId is not set, quit
    const classifiedId: number|null = store.getters["FormModule/getClassifiedId"];
    if (classifiedId === null) {
        return Promise.reject("Cannot save pictures for unknown classified");
    }

    // API settings
    const baseUrl: string = process.env.VUE_APP_CLASSIFIEDS_MANAGEMENT_URL || "";
    const version = "1";
    const baseUri = `${baseUrl}${version}/classifieds/${classifiedId}`;

    const currentCustomer = store.getters["CustomersModule/getCurrentCustomer"];

    const headers: Record<string, string> = {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
    };

    if (currentCustomer) {
        headers["customerId"] = currentCustomer.id;
    }

    try {
        // Get presigned URL
        const presignRes = await axios.post(`${baseUri}/uploads`, {}, { headers: headers });
        const { path, id } = presignRes.data;

        if (!path || !id) {
            // remove erroneous picture
            store.dispatch("PicturesModule/removePicture", picture);
            app.dialog.alert(i18n.t("server_not_responding_error").toString());
            return false;
        }

        // Upload file to S3 using presigned URL
        const formData = new FormData();
        Object.entries(path.fields).forEach(([key, value]) => formData.append(key, value as string));
        formData.append("file", file);

        const uploadRes = await fetch(path.url, {
            method: "POST",
            body: formData
        });

        if (!uploadRes.ok) {
            // remove erroneous picture
            store.dispatch("PicturesModule/removePicture", picture);
            app.dialog.alert(i18n.t("server_not_responding_error").toString());
            return false;
        }
        let status: string | null = null;
        let errorCount = 0;
        const MAX_UPLOAD_ATTEMPTS = 10;
        const MAX_UPLOAD_ERRORS = 5;

        for (let attempt = 0; attempt < MAX_UPLOAD_ATTEMPTS; attempt++) {
            await delay(1000); // 1-second interval between checks

            try {
                const res = await axios.get(`${baseUri}/uploads/${id}`, { headers: headers});
                status = res.data.status;

                if (status === "SUCCESSFUL" || status === "FAILED") {
                    break;
                }

            } catch (err) {
                errorCount++;
                if (errorCount >= MAX_UPLOAD_ERRORS) {
                    break;
                }
            }
        }

        if (status !== "SUCCESSFUL") {
            app.dialog.alert(i18n.t("server_not_responding_error").toString());
            return false;
        }

        const pictureDetails = {
            uploadId: id,
            type: picture.type,
        };

        const finalizeRes = await axios.post(`${baseUri}/photos`, pictureDetails, { headers: headers });

        const data = finalizeRes.data;

        if (data.errors && data.errors.length) {
            store.commit("PicturesModule/setErrorMessage", data.errors[0]?.type);
            return false;
        }

        // remove old picture
        store.dispatch("PicturesModule/removePicture", picture);

        // update picture object from response
        picture.id = data.id;
        picture.url = data.url;
        picture.isLoading = false;

        // save new picture in store
        store.commit("PicturesModule/addPicture", picture);

        // save picture to delete if the user clicks on cancel
        store.dispatch("PicturesModule/addDeleteOnCancel", data.id);

        return true;
    } catch (error: any) {
        // remove erroneous picture
        store.dispatch("PicturesModule/removePicture", picture);

        // set error message
        store.commit("PicturesModule/setErrorMessage", error.response.data.errors[0]?.type);
        return false;
    }
}

export function deleteRemotePictures(cancelAction = false) : Promise<boolean> {
    // if classifiedId is not set, quit
    const classifiedId: number|null = store.getters["FormModule/getClassifiedId"];
    if (classifiedId == null) {
        return Promise.resolve(false);
    }

    // Get deleted pictures. If array empty allow to orderPictures
    const deletedPictures: Array<string> = cancelAction ? store.getters["PicturesModule/getDeleteOnCancel"] : store.getters["PicturesModule/getDeletedPictures"];
    if(deletedPictures.length === 0) {
        store.dispatch("PicturesModule/resetDeleteOnCancel");
        return Promise.resolve(true);
    }

    const currentCustomer = store.getters["CustomersModule/getCurrentCustomer"];

    const headers: Record<string, unknown> = {
        "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
    };

    if(currentCustomer) {
        headers["customerId"] = currentCustomer.id;
    }

    // API settings
    const baseUrl: string = process.env.VUE_APP_CLASSIFIEDS_MANAGEMENT_URL || "";
    const uri = `/classifieds/${classifiedId}/photos?ids=${deletedPictures.join(",")}`;
    const version = "1";
    const method = "delete";

    return axios({
        method: method,
        url: baseUrl + version + uri,
        headers: headers
    }).then(() => {
        store.dispatch("PicturesModule/resetDeletedPictures");
        store.dispatch("PicturesModule/resetDeleteOnCancel");
        return true;
    }).catch(error => {
        throw new Error(error);
    });
}

function publishPictures(): Promise<boolean> {
    // if classifiedId is not set, quit
    const classifiedId: number|null = store.getters["FormModule/getClassifiedId"];
    if (classifiedId == null) {
        return Promise.resolve(false);
    }

    const currentCustomer = store.getters["CustomersModule/getCurrentCustomer"];

    const headers: Record<string, unknown> = {
        "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
    };

    if(currentCustomer) {
        headers["customerId"] = currentCustomer.id;
    }

    const baseUrl: string = process.env.VUE_APP_CLASSIFIEDS_MANAGEMENT_URL || "";
    const uri = `/classifieds/${classifiedId}/photos/publish`;
    const version = "1";
    const method = "post";

    return axios({
        method: method,
        url: baseUrl + version + uri,
        headers: headers
    }).then(() => {
        return true;
    }).catch(error => {
        throw new Error(error);
    });

}

export function deleteOrderPublishPictures(shouldPublish: boolean): Promise<boolean> {
    return deleteRemotePictures()
        .then(res => {
            if(res) {
                return persistPicturesOrder().then(res => {
                    if(res && shouldPublish) {
                        return publishPictures().then(res => res);
                    } else {
                        return false;
                    }
                });
            } else {
                return false;
            }
        }).catch(error => {
            throw new Error(error);
        });
}
